import { Button } from '~/components/general/Button'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

import { useEffect } from 'react'
import { trackEvent } from '~/utils/tracking'
import { createOptimizedContentfulImage } from '~/libs/contentful/helpers'
import { FlexContainer } from '~/components/Grid'
import { TypeHomePageBlock } from '~/types/contentful'
import { Asset } from 'contentful'

gsap.registerPlugin(ScrollTrigger)

type ExcelInMyCareerProps = {
  section: TypeHomePageBlock<'WITHOUT_UNRESOLVABLE_LINKS', string>
}

export const ExcelInMyCareer = ({ section }: ExcelInMyCareerProps) => {
  const data = section.fields

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          start: 'top center',
          trigger: '.animate-cards-trigger',
        },
      })
      tl.fromTo(
        '.animate-card1',
        1,
        {
          clipPath: 'inset(100% 0% 0% 0%)',
        },
        {
          clipPath: 'inset(0% 0% 0% 0%)',
        },
      )
        .fromTo(
          '.animate-card2',
          1,
          {
            clipPath: 'inset(100% 0% 0% 0%)',
          },
          {
            clipPath: 'inset(0% 0% 0% 0%)',
          },
          '-=1',
        )
        .fromTo(
          '.animate-card3',
          1,
          {
            clipPath: 'inset(100% 0% 0% 0%)',
            y: 200,
          },
          {
            clipPath: 'inset(0% 0% 0% 0%)',
            y: 0,
          },
          '-=1',
        )
        .fromTo(
          '.animate-card4',
          1,
          {
            clipPath: 'inset(100% 0% 0% 0%)',
            y: 200,
          },
          {
            clipPath: 'inset(0% 0% 0% 0%)',
            y: 0,
          },
          '-=1',
        )
        .fromTo(
          '.animate-card5',
          1,
          {
            clipPath: 'inset(100% 0% 0% 0%)',
          },
          {
            clipPath: 'inset(0% 0% 0% 0%)',
          },
          '-=1',
        )
        .fromTo(
          '.animate-woman',
          1.4,
          {
            transformOrigin: '0% 100%',
            transform: 'scale(0.91)',
          },
          {
            transform: 'scale(1)',
          },
          '-=1',
        )
    })

    return () => ctx.revert()
  }, [])

  const handleCTABtnClick = () => {
    trackEvent('homepage_click_cta', {
      block: data.blockId,
      target: data.buttonLink as string,
      homepage_CTA: data.buttonName as string,
    })
  }

  return (
    <>
      <section
        className="max-lg:my-0 max-lg:-mx-4 max-md:p-[44px_16px_36px] max-md:m-[79px_-18px_60px] min-h-[658px] max-w-[1376px] p-[79px_159px_79px_128px] max-xl:gap-4 max-xl:m-[100px_-18px_40px] max-xl:p-[135px_110px_134px_72px] gap-36 flex relative mx-auto my-25 rounded-[28px] max-lg:flex-col-reverse max-lg:gap-10 max-md:overflow-hidden"
        data-block-id={data.blockId}
        id={data.blockId}
        style={{
          backgroundImage: data.images
            ? `url(${createOptimizedContentfulImage({ file: (data.images[0] as Asset<'WITHOUT_UNRESOLVABLE_LINKS', string>).fields.file! })})`
            : '',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <FlexContainer
          justify={'start'}
          style={{ zIndex: '2', maxWidth: '420px' }}
          className="max-lg:my-0 max-lg:mx-auto max-lg:max-w-[514px] relative max-md:items-stretch justify-between flex-col items-center"
        >
          <FlexContainer
            justify={'between'}
            className="max-lg:my-0 max-lg:mx-auto max-lg:max-w-[514px] relative justify-between flex-col items-start max-md:items-stretch"
          >
            <h2 className="text-2xl max-md:mr-0 max-md:mb-4 max-lg:mb-8 max-xl:mb-[-100px] mb-10">
              {data.title}
            </h2>
            <p className="max-md:mb-4 max-lg:mb-7 mb-10 text-s">{data.description}</p>
            <Button
              text={data.buttonName}
              type={'link'}
              href={data.buttonLink}
              onClick={handleCTABtnClick}
              className={'w-fit'}
            />
          </FlexContainer>
        </FlexContainer>

        <FlexContainer
          className="relative md:justify-between max-md:justify-center gap-4 max-w-fit animate-cards-trigger max-lg:my-0 max-lg:mx-auto "
          flexWrap={'nowrap'}
          justify={'start'}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignSelf: 'flex-end',
              gap: '15px',
              height: 'fit-content',
            }}
          >
            <img
              src="/images/minicard1.jpg"
              className={
                'animate-card1 max-md:max-w-[81px] max-w-[171px] rounded-lg overflow-hidden block w-full h-auto'
              }
              alt=""
              loading="lazy"
            />
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              gap: '15px',
            }}
          >
            <img
              src="/images/minicard3.jpg"
              className={
                'animate-card3 max-md:max-w-[81px] max-w-[171px] rounded-lg overflow-hidden block w-full h-auto'
              }
              alt=""
              loading="lazy"
            />

            <img
              src="/images/minicard2.jpg"
              className={
                'animate-card2 max-md:max-w-[81px] max-w-[171px] rounded-lg overflow-hidden block w-full h-auto'
              }
              alt=""
              loading="lazy"
            />
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              gap: '15px',
              position: 'relative',
            }}
          >
            <img
              src="/images/minicard4.jpg"
              className={
                'animate-card4 max-md:max-w-[81px] max-w-[171px] rounded-lg overflow-hidden block w-full h-auto'
              }
              alt=""
              loading="lazy"
            />

            <img
              src="/images/minicard5.jpg"
              className={
                'animate-card5 max-md:max-w-[81px] max-w-[171px] rounded-lg overflow-hidden block w-full h-auto'
              }
              alt=""
              loading="lazy"
            />
            <div className="absolute bottom-[-10%] right-[-54%] z-[2] max-md:w-[100px] max-md:right-[-15%] max-md:bottom-[-15%]">
              <img
                src="/images/woman_icon.png"
                className={'animate-woman block w-full h-auto'}
                alt=""
                loading="lazy"
              />
            </div>

            <div className="absolute bottom-[32%] z-2 max-xl:bottom-[39%] max-lg:bottom-[40%] max-md:w-8 max-md:right-[-25%] -right-[55%]">
              <img
                src="/images/star_for woman_icon.png"
                className={'animate-card-star block w-full h-auto'}
                alt=""
                loading="lazy"
              />
            </div>
          </div>
        </FlexContainer>
      </section>
    </>
  )
}
